import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/common/Button'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionTitle } from '@/components/common/SectionTitle'
import { Toolbar } from '@/components/common/Toolbar'
import { usePersonas, type PersonaListItem } from '@/hooks/usePersonas'
import { useRelationships } from '@/hooks/useRelationships'
import { useApiClient } from '@/hooks/useApiClient'
import { useCommercialSignals } from '@/hooks/useCommercialSignals'
import './prototype.css'
import './hoy.css'

const roleLabel: Record<NonNullable<PersonaListItem['rolEnRelacion']>, string> = {
  decisor: 'Decisor',
  influenciador: 'Influenciador',
  operativo: 'Operativo',
  sponsor: 'Sponsor',
  otro: 'Otro',
}

type RoleFilter = 'todas' | 'decisor' | 'influenciador' | 'operativo' | 'conCorreo' | 'sinCorreo' | 'sinCargo'
type PresentationMode = 'lista' | 'contexto'
type PersonSortKey = 'relevancia' | 'nombre' | 'empresa' | 'rol' | 'ultima' | 'proximo' | 'salud'

const ROLE_FILTER_LABEL: Record<RoleFilter, string> = {
  todas: 'Todas',
  decisor: 'Decisores',
  influenciador: 'Influenciadores',
  operativo: 'Operativos',
  conCorreo: 'Con correo',
  sinCorreo: 'Sin correo',
  sinCargo: 'Sin cargo',
}

const ROLE_FILTER_ORDER: RoleFilter[] = ['todas', 'decisor', 'influenciador', 'operativo', 'conCorreo', 'sinCorreo', 'sinCargo']
const PRIMARY_ROLE_FILTERS: RoleFilter[] = ['todas', 'decisor', 'influenciador', 'operativo']
const SECONDARY_ROLE_FILTERS = ROLE_FILTER_ORDER.filter((filter) => !PRIMARY_ROLE_FILTERS.includes(filter))
const PAGE_SIZE = 24

function isRoleFilter(value: string | null): value is RoleFilter {
  return !!value && (ROLE_FILTER_ORDER as string[]).includes(value)
}

export const Personas: React.FC = () => {
  const { status, personas, error, reload } = usePersonas()
  const { status: relStatus, relationships } = useRelationships()
  const { makeClient } = useApiClient()
  const commercialSignals = useCommercialSignals()
  const [searchParams] = useSearchParams()
  const [search, setSearch] = React.useState('')
  const [organization, setOrganization] = React.useState('todas')
  const [roleFilter, setRoleFilter] = React.useState<RoleFilter>(
    isRoleFilter(searchParams.get('filtro')) ? (searchParams.get('filtro') as RoleFilter) : 'todas'
  )
  const [outlookContacts, setOutlookContacts] = React.useState<Array<{ id: string; title: string; email: string; cargo: string | null }>>([])
  const [outlookChecked, setOutlookChecked] = React.useState(false)
  const [outlookLoading, setOutlookLoading] = React.useState(false)
  const [outlookError, setOutlookError] = React.useState<string | null>(null)
  const [organizationByContact, setOrganizationByContact] = React.useState<Record<string, string>>({})
  const [presentationMode, setPresentationMode] = React.useState<PresentationMode>('contexto')
  const [sort, setSort] = React.useState<{ key: PersonSortKey; direction: 'asc' | 'desc' }>({ key: 'relevancia', direction: 'asc' })
  const [page, setPage] = React.useState(1)

  const checkUnlinkedOutlookContacts = async () => {
    const client = makeClient()
    if (!client || outlookLoading) return
    setOutlookLoading(true)
    setOutlookError(null)
    try {
      const response = await client.get<{ contacts: Array<{ id: string; title: string; email: string; cargo: string | null }> }>('/api/outlook-contacts/unlinked')
      setOutlookContacts(response.contacts)
      setOutlookChecked(true)
    } catch {
      setOutlookError('Outlook no está disponible para revisar contactos sin vínculo.')
    } finally {
      setOutlookLoading(false)
    }
  }

  const importOutlookContact = async (contact: { id: string; title: string; email: string }) => {
    const organizacionId = organizationByContact[contact.id]
    const organizationName = relationships.find((relationship) => relationship.id === organizacionId)?.title
    if (!organizacionId || !organizationName) return
    if (!window.confirm(`¿Incorporar a ${contact.title} (${contact.email}) como Persona de ${organizationName}? Se creará una sola Persona y un vínculo confirmado.`)) return
    const client = makeClient()
    if (!client) return
    setOutlookLoading(true)
    setOutlookError(null)
    try {
      const result = await client.post<{synchronization:{verified?:boolean}}>(`/api/outlook-contacts/${encodeURIComponent(contact.id)}/personas`, { organizacionId, title: contact.title })
      if (!result.synchronization.verified) setOutlookError('Persona guardada en Sales; su vínculo Microsoft aún no está confirmado. Compruébalo desde la ficha de la persona.')
      setOutlookContacts((items) => items.filter((item) => item.id !== contact.id))
      reload()
    } catch {
      setOutlookError('No se pudo incorporar este contacto. Sales no modificó ningún contacto Outlook.')
    } finally {
      setOutlookLoading(false)
    }
  }

  const organizations = ['todas', ...new Set(personas.map((p) => p.organizacionNombre).filter((n): n is string => Boolean(n)))]

  const cobertura = React.useMemo(() => {
    const empresasConPersona = new Set(personas.map((p) => p.organizacionId).filter(Boolean))
    const totalEmpresas = relationships.length
    return {
      total: personas.length,
      empresasConPersona: empresasConPersona.size,
      empresasSinPersona: relStatus === 'ready' ? Math.max(0, totalEmpresas - empresasConPersona.size) : null,
      totalEmpresas,
      decisores: personas.filter((p) => p.rolEnRelacion === 'decisor').length,
      influenciadores: personas.filter((p) => p.rolEnRelacion === 'influenciador').length,
      operativos: personas.filter((p) => p.rolEnRelacion === 'operativo').length,
      conCorreo: personas.filter((p) => p.email).length,
      sinCargo: personas.filter((p) => !p.cargo).length,
    }
  }, [personas, relationships, relStatus])

  const filtered = personas.filter((p) => {
    const haystack = [p.title, p.cargo, p.email, p.organizacionNombre].filter(Boolean).join(' ').toLowerCase()
    const matchesSearch = !search || haystack.includes(search.toLowerCase())
    const matchesOrganization = organization === 'todas' || p.organizacionNombre === organization

    let matchesRole = true
    if (roleFilter === 'decisor') matchesRole = p.rolEnRelacion === 'decisor'
    else if (roleFilter === 'influenciador') matchesRole = p.rolEnRelacion === 'influenciador'
    else if (roleFilter === 'operativo') matchesRole = p.rolEnRelacion === 'operativo'
    else if (roleFilter === 'conCorreo') matchesRole = Boolean(p.email)
    else if (roleFilter === 'sinCorreo') matchesRole = !p.email
    else if (roleFilter === 'sinCargo') matchesRole = !p.cargo

    return matchesSearch && matchesOrganization && matchesRole
  })

  const relationshipById = React.useMemo(
    () => new Map(relationships.map((relationship) => [relationship.id, relationship])),
    [relationships],
  )
  const signalsPorPersona = React.useMemo(() => {
    const map = new Map<string, number>()
    for (const signal of commercialSignals.signals) {
      if (!signal.personId) continue
      map.set(signal.personId, (map.get(signal.personId) ?? 0) + 1)
    }
    return map
  }, [commercialSignals.signals])

  const commercialTuple = (person: PersonaListItem): Array<number | string> => {
    const relationship = person.organizacionId ? relationshipById.get(person.organizacionId) : null
    const priority = relationship?.prioridadComercial === 'p1' ? 0 : relationship?.prioridadComercial === 'p2' ? 1 : relationship?.prioridadComercial === 'p3' ? 2 : 3
    const signal = relationship?.signal === 'requiere_atencion' ? 0 : relationship?.signal === 'sin_contexto_suficiente' ? 1 : relationship?.signal === 'al_dia' ? 2 : 3
    const role = person.rolEnRelacion === 'sponsor' ? 0 : person.rolEnRelacion === 'decisor' ? 1 : person.rolEnRelacion === 'influenciador' ? 2 : person.rolEnRelacion === 'operativo' ? 3 : 4
    return [priority, signal, relationship?.nextAction ? 1 : 0, role, relationship?.lastActivity ?? '', person.title]
  }

  const sorted = React.useMemo(() => {
    const valueFor = (person: PersonaListItem): string => {
      const relationship = person.organizacionId ? relationshipById.get(person.organizacionId) : null
      switch (sort.key) {
        case 'empresa': return person.organizacionNombre ?? ''
        case 'rol': return person.rolEnRelacion ? roleLabel[person.rolEnRelacion] : ''
        case 'ultima': return relationship?.lastActivity ?? ''
        case 'proximo': return relationship?.nextAction ?? ''
        case 'salud': return relationship?.signal ?? ''
        default: return person.title
      }
    }
    return [...filtered].sort((left, right) => {
      let comparison = 0
      if (sort.key === 'relevancia') {
        const a = commercialTuple(left)
        const b = commercialTuple(right)
        for (let index = 0; index < a.length && comparison === 0; index += 1) {
          comparison = typeof a[index] === 'number' && typeof b[index] === 'number'
            ? Number(a[index]) - Number(b[index])
            : String(a[index]).localeCompare(String(b[index]), 'es', { sensitivity: 'base' })
        }
      } else {
        comparison = valueFor(left).localeCompare(valueFor(right), 'es', { sensitivity: 'base' })
      }
      return sort.direction === 'asc' ? comparison : -comparison
    })
  }, [filtered, relationshipById, sort])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const visiblePeople = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  React.useEffect(() => {
    setPage(1)
  }, [roleFilter, organization, search, presentationMode, sort.key, sort.direction])

  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const toggleSort = (key: PersonSortKey) => {
    setSort((current) => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' })
  }

  const sortLabel = (key: PersonSortKey, label: string) => (
    <button type="button" className="prototypeListTable__sort" onClick={() => toggleSort(key)}>
      {label}{sort.key === key ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  )

  const personCard = (person: PersonaListItem, mobile = false) => {
    const relationship = person.organizacionId ? relationshipById.get(person.organizacionId) : null
    return (
      <article key={`${mobile ? 'mobile-' : ''}${person.id}`} className={`prototypePersonaRow commercialPersonCard${mobile ? ' commercialPersonCard--mobile' : ''}`}>
        <div className="prototypePersonaRow__main">
          <div className="prototypePersonaRow__topline">
            <Link to={`/personas/${person.id}`} className="prototypePersonaRow__title">{person.title}</Link>
            {person.rolEnRelacion && <span className="empresasChip">{roleLabel[person.rolEnRelacion]}</span>}
            {(signalsPorPersona.get(person.id) ?? 0) > 0 && <span className="commercialSignalBadge">{signalsPorPersona.get(person.id)} señal{signalsPorPersona.get(person.id) === 1 ? '' : 'es'}</span>}
          </div>
          <div className="prototypePersonaRow__meta">
            <span>{person.cargo ?? 'Cargo no registrado'}</span>
            {person.organizacionId && <Link to={`/relaciones/${person.organizacionId}`} className="prototypeLink">{person.organizacionNombre ?? 'Ver empresa'}</Link>}
          </div>
          <div className="commercialDecisionGrid commercialDecisionGrid--person">
            <div><span className="prototypeMiniLabel">Salud de la relación</span><strong>{relationship ? relationship.signal === 'al_dia' ? 'Al día' : relationship.signal === 'requiere_atencion' ? 'Requiere atención' : relationship.signal === 'pausada' ? 'En pausa' : 'Contexto insuficiente' : 'Sin relación'}</strong></div>
            <div><span className="prototypeMiniLabel">Última actividad válida</span><strong>{relationship?.lastActivity ? new Date(relationship.lastActivity).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Sin actividad verificable'}</strong></div>
            <div><span className="prototypeMiniLabel">Siguiente acción</span><strong>{relationship?.nextAction ?? 'Definir próxima acción'}</strong></div>
          </div>
        </div>
        <div className="prototypePersonaRow__aside">
          <Link to={`/personas/${person.id}`} className="commercialPrimaryLink">Abrir contexto</Link>
          {person.organizacionId && <Link to={`/relaciones/${person.organizacionId}#nueva-accion`} className="prototypeLink">Registrar próxima acción</Link>}
        </div>
      </article>
    )
  }

  const pagination = status === 'ready' && sorted.length > PAGE_SIZE ? (
    <nav className="commercialPagination" aria-label="Paginación de personas">
      <Button size="sm" variant="secondary" disabled={page === 1} onClick={() => setPage((current) => current - 1)}>Anterior</Button>
      <span>Página {page} de {totalPages} · {sorted.length} personas</span>
      <Button size="sm" variant="secondary" disabled={page === totalPages} onClick={() => setPage((current) => current + 1)}>Siguiente</Button>
    </nav>
  ) : null

  const renderBody = () => {
    if (status === 'loading') {
      return (
        <div className="commercialLoadingState" role="status" aria-live="polite" aria-busy="true">
          <strong>Preparando personas y contexto comercial…</strong>
          <span />
          <span />
          <span />
        </div>
      )
    }
    if (status === 'not_signed_in') {
      return <p className="prototypeRelationshipRow__summary">Inicia sesión con tu cuenta Microsoft para ver personas en contexto.</p>
    }
    if (status === 'not_configured') {
      return (
        <p className="prototypeRelationshipRow__summary">
          La conexión con SharePoint aún no está habilitada en este entorno.
        </p>
      )
    }
    if (status === 'forbidden') {
      return (
        <p className="prototypeRelationshipRow__summary">
          Tu identidad es válida pero tu perfil aún no está habilitado en Wacner Sales.
        </p>
      )
    }
    if (status === 'unauthorized' || status === 'error') {
      return (
        <div>
          <p className="prototypeRelationshipRow__summary">
            No se pudieron cargar las personas{error instanceof Error ? `: ${error.message}` : '.'}
          </p>
          <Button size="sm" variant="secondary" onClick={reload}>Reintentar</Button>
        </div>
      )
    }
    if (filtered.length === 0) {
      return (
        <p className="prototypeRelationshipRow__summary">
          {personas.length === 0
            ? 'Aún no hay personas registradas. Se vinculan desde la ficha de cada relación a medida que se identifican contactos reales.'
            : 'Ninguna persona coincide con el filtro o la búsqueda.'}
        </p>
      )
    }

    if (presentationMode === 'lista') {
      return (
        <>
        <div className="prototypeListTable__wrap commercialDesktopOnly">
          <table className="prototypeListTable">
            <thead>
              <tr>
                <th>{sortLabel('relevancia', 'Relevancia')}</th>
                <th>{sortLabel('nombre', 'Nombre')}</th>
                <th>{sortLabel('empresa', 'Empresa')}</th>
                <th>{sortLabel('rol', 'Rol')}</th>
                <th>{sortLabel('salud', 'Salud')}</th>
                <th>{sortLabel('ultima', 'Última actividad')}</th>
                <th>{sortLabel('proximo', 'Siguiente acción')}</th>
              </tr>
            </thead>
            <tbody>
              {visiblePeople.map((person, index) => {
                const relationship = person.organizacionId ? relationshipById.get(person.organizacionId) : null
                return (
                  <tr key={person.id}>
                    <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                    <td><Link to={`/personas/${person.id}`} className="prototypeListTable__primary">{person.title}</Link>{(signalsPorPersona.get(person.id) ?? 0) > 0 && <> <span className="commercialSignalBadge">{signalsPorPersona.get(person.id)}</span></>}</td>
                    <td>{person.organizacionId ? <Link to={`/relaciones/${person.organizacionId}`} className="prototypeLink">{person.organizacionNombre ?? 'Ver empresa'}</Link> : 'Sin empresa'}</td>
                    <td>{person.rolEnRelacion ? roleLabel[person.rolEnRelacion] : '—'}</td>
                    <td>{relationship?.signal === 'al_dia' ? 'Al día' : relationship?.signal === 'requiere_atencion' ? 'Requiere atención' : relationship?.signal === 'pausada' ? 'En pausa' : 'Contexto insuficiente'}</td>
                    <td>{relationship?.lastActivity ? new Date(relationship.lastActivity).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Sin actividad verificable'}</td>
                    <td>{relationship?.nextAction ?? 'Definir próxima acción'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="commercialMobileList">{visiblePeople.map((person) => personCard(person, true))}</div>
        {pagination}
        </>
      )
    }

    return (
      <div className="prototypePersonaTray">
        {visiblePeople.map((person) => personCard(person))}
        {pagination}
      </div>
    )
  }

  return (
    <div className="prototypePage">
      <PageHeader
        title="Personas en contexto"
        subtitle="Quién requiere atención, en qué cuenta y cuál es el siguiente paso."
      />

      {status === 'ready' && (
        <div className="empresasSummary">
          <span className="hoy__stat hoy__stat--static">
            <strong>{cobertura.total}</strong>
            <span>Personas totales</span>
          </span>
          {cobertura.empresasSinPersona !== null && (
            <Link to="/relaciones?filtro=sinPersona" className="hoy__stat">
              <strong>{cobertura.empresasSinPersona}</strong>
              <span>Empresas sin personas</span>
            </Link>
          )}
          <button className="hoy__stat" onClick={() => setRoleFilter('decisor')}>
            <strong>{cobertura.decisores}</strong>
            <span>Decisores</span>
          </button>
        </div>
      )}

      {status === 'ready' && (
        <p className="prototypeRelationshipRow__summary">
          {cobertura.total} persona{cobertura.total === 1 ? '' : 's'} identificada{cobertura.total === 1 ? '' : 's'} para{' '}
          {cobertura.totalEmpresas || '—'} empresa{cobertura.totalEmpresas === 1 ? '' : 's'}. Próximo trabajo: identificar
          decisores y operadores en cuentas prioritarias.
        </p>
      )}

      <div className="prototypeHero__tagRow" style={{ marginBottom: 4 }}>
        {PRIMARY_ROLE_FILTERS.map((key) => (
          <button
            key={key}
            className={`prototypeFilter ${roleFilter === key ? 'prototypeFilter--active' : ''}`}
            onClick={() => setRoleFilter(key)}
          >
            {ROLE_FILTER_LABEL[key]}
          </button>
        ))}
      </div>

      <Toolbar searchPlaceholder="Buscar persona, empresa o cargo..." onSearch={setSearch} />

      <details className="commercialMoreFilters">
        <summary>Más filtros</summary>
        <div className="commercialMoreFilters__content">
          {SECONDARY_ROLE_FILTERS.map((key) => <button key={key} className={`prototypeFilter ${roleFilter === key ? 'prototypeFilter--active' : ''}`} onClick={() => setRoleFilter(key)}>{ROLE_FILTER_LABEL[key]}</button>)}
          {organizations.map((item) => <button key={item} className={`prototypeFilter ${organization === item ? 'prototypeFilter--active' : ''}`} onClick={() => setOrganization(item)}>{item}</button>)}
        </div>
      </details>

      <details className="commercialAdminPanel">
        <summary>Administración de contactos Outlook</summary>
        <section className="prototypeSection" aria-labelledby="outlook-import-title">
          <SectionTitle title="Contactos Outlook sin vínculo" subtitle="Revisión manual de contactos que todavía no existen en Sales." />
        {!outlookChecked ? (
          <Button size="sm" variant="secondary" disabled={outlookLoading} onClick={checkUnlinkedOutlookContacts}>
            {outlookLoading ? 'Revisando Outlook…' : 'Revisar contactos Outlook'}
          </Button>
        ) : outlookContacts.length === 0 ? (
          <p className="prototypeRelationshipRow__summary">No hay contactos Outlook sin vínculo que puedan incorporarse de forma segura.</p>
        ) : (
          <div className="prototypePersonaTray">
            {outlookContacts.map((contact) => (
              <article key={contact.id} className="prototypePersonaRow">
                <div className="prototypePersonaRow__main">
                  <div className="prototypePersonaRow__topline"><strong>{contact.title}</strong></div>
                  <div className="prototypePersonaRow__meta"><span>{contact.cargo ?? 'Cargo no registrado'}</span><span>{contact.email}</span></div>
                </div>
                <div className="prototypePersonaRow__aside">
                  <select
                    aria-label={`Empresa para ${contact.title}`}
                    value={organizationByContact[contact.id] ?? ''}
                    onChange={(event) => setOrganizationByContact((current) => ({ ...current, [contact.id]: event.target.value }))}
                  >
                    <option value="">Selecciona empresa…</option>
                    {relationships.map((relationship) => <option key={relationship.id} value={relationship.id}>{relationship.title}</option>)}
                  </select>
                  <Button size="sm" disabled={!organizationByContact[contact.id] || outlookLoading} onClick={() => importOutlookContact(contact)}>
                    Incorporar a Sales
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
        {outlookError && <p className="prototypeRelationshipRow__summary" role="status">{outlookError}</p>}
        </section>
      </details>

      <section className="prototypeSection">
        <SectionTitle
          title={status === 'ready' ? `${filtered.length} personas en contexto` : 'Personas'}
          subtitle="Orden comercial por prioridad de cuenta, salud, próxima acción y rol de la persona."
          action={
            <div className="prototypeContextCard__actions" role="group" aria-label="Vista de personas">
              <Button size="sm" variant={presentationMode === 'contexto' ? 'primary' : 'secondary'} onClick={() => setPresentationMode('contexto')}>Contexto</Button>
              <Button size="sm" variant={presentationMode === 'lista' ? 'primary' : 'secondary'} onClick={() => setPresentationMode('lista')}>Tabla</Button>
            </div>
          }
        />
        {renderBody()}
      </section>
    </div>
  )
}
